import React, { useState, useContext, useEffect, useCallback } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { CardList } from "../../ui/Card";
import Loader from "../../ui/Loader";
import Modal from "../../ui/Modal";
import { AuthContext } from "../../../context/AuthContext";
import {
  swalConfirm,
  swalError,
  swalSuccess,
  swalWarning,
} from "../../../utils/swalAlert";
import { fetchWithAuth } from "../../../utils/api";
import ModalTableInput from "../../ui/ModalTableInput";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const InstructorClasses = () => {
  const { user, logout } = useContext(AuthContext);
  const id = user._id;
  const navigate = useNavigate();

  const [institute, setInstitute] = useState(null);
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isCOPOEnabled, setIsCOPOEnabled] = useState(false);
  const [levels, setLevels] = useState([]);
  const [levelsFetched, setLevelsFetched] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [attainmentMap, setAttainmentMap] = useState({});
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isIndirectAttainment, setIsIndirectAttainment] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newClass, setNewClass] = useState({
    courseId: "",
    periodId: "",
    instructorId: user._id,
    enrollmentEndDate: "",

    name: "",

    level: null,
  });
  const [editClassData, setEditClassData] = useState(null);
  const [initialEditClassData, setInitialEditClassData] = useState(null);
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [formError, setFormError] = useState("");

  const [coursesFetched, setCoursesFetched] = useState(false);
  const [periodsFetched, setPeriodsFetched] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedLevelFilter, setSelectedLevelFilter] = useState(null);
  const [initialPeriodDetermined, setInitialPeriodDetermined] = useState(false);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingClasses, setDeletingClasses] = useState(new Set());
  const [syncingClasses, setSyncingClasses] = useState(new Set());
  const [computingAttainment, setComputingAttainment] = useState(new Set());
  const [courseHasProgram, setCourseHasProgram] = useState({});

  const convertToUserTimezone = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() + tzOffset).toISOString();
  };

  const userDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const tzOffset = date.getTimezoneOffset() * 60000;
    if (tzOffset > 0) {
      return new Date(date.getTime() + tzOffset).toISOString().split("T")[0];
    } else {
      return new Date(date.getTime() - tzOffset).toISOString().split("T")[0];
    }
  };

  // Fetch institute Level data
  useEffect(() => {
    const fetchInstituteLevelData = async () => {
      try {
        const instituteData = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${user.instituteId}`,
        );
        if (!instituteData) return;
        if (!instituteData.ok)
          throw new Error("Failed to fetch institute data");
        const data = await instituteData.json();
        setInstitute(data);
        setIsCOPOEnabled(data.isCOPOEnabled || false);

        if (data.subjects && data.subjects.length > 0) {
          setSubjects(
            data.subjects.map((subject) => ({
              value: subject,
              label: subject,
            })),
          );
        }

        if (data.levels && data.levels.length > 0) {
          setLevels(
            data.levels.map((level) => ({
              value: level,
              label: level,
            })),
          );
          setLevelsFetched(true);
        } else {
          try {
            if (levelsFetched) return;
            const response = await fetchWithAuth(
              `${serverUrl}/api/lov/get-all-by-type/level`,
              {
                credentials: "include",
              },
            );
            if (!response) return;
            if (!response.ok) throw new Error("Failed to fetch levels");
            const data = await response.json();
            setLevels(
              data
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((level) => ({
                  value: level.value,
                  label: level.value,
                })),
            );
            setLevelsFetched(true);
          } catch (err) {
            swalError(err.message || "Failed to fetch institute Data");
          }
        }
      } catch (err) {
        swalError(err.message);
      }
    };
    fetchInstituteLevelData();
  }, [user.instituteId, levelsFetched]); // Removed hasLevels to prevent infinite loop

  const fetchClassAttainment = useCallback(async (classId) => {
    try {
      const aggRes = await fetchWithAuth(
        `${serverUrl}/api/class/getAttainment/${classId}`,
        { credentials: "include" },
      );
      if (!aggRes) return;
      if (!aggRes.ok) {
        return; // Silently skip if no data
      }
      const aggData = await aggRes.json();
      if (
        aggData &&
        aggData.attainment &&
        Array.isArray(aggData.attainment) &&
        aggData.attainment.length > 0
      ) {
        const overall = Number(aggData.overallAverage) || 0;
        const roundedOverall = Number(overall.toFixed(2));
        const details = aggData.attainment
          .map((r) => `${r.coLevel || "N/A"}: ${r.totalCOAttainment}`)
          .join(", ");
        setAttainmentMap((prev) => ({
          ...prev,
          [classId]: { overall: roundedOverall, details },
        }));
      }
    } catch (err) {
      // Silently handle errors
    }
  }, []);

  const fetchClasses = useCallback(async () => {
    try {
      setLoading(true);
      const periodQuery = selectedPeriod?.value && selectedPeriod.value !== 'all' ? `?periodId=${selectedPeriod.value}` : "";
      const response = await fetchWithAuth(
        `${serverUrl}/api/class/get-all/${id}${periodQuery}`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch classes");
      const data = await response.json();
      const enrichedClasses = (Array.isArray(data) ? data : [])
        .filter((cls) => !cls.isLevelPlacementZone)
        .map((cls) => ({
          id: cls._id,
          ...cls,
        }));
      setClasses(enrichedClasses);

      // prune attainmentMap to keep only entries for currently loaded classes
      setAttainmentMap((prev) => {
        const kept = {};
        enrichedClasses.forEach((cls) => {
          if (prev[cls.id || cls._id] !== undefined)
            kept[cls.id || cls._id] = prev[cls.id || cls._id];
        });
        return kept;
      });
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.error);
    } finally {
      setLoading(false);
    }
  }, [id, selectedPeriod]);

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/course/get-all/${user.instituteId}`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch courses");
      const data = await response.json();
      setCourses(data);
      if (isCOPOEnabled) {
        const programMap = {};
        data.forEach((course) => {
          programMap[course.id || course._id] = !!course.programId;
        });
        setCourseHasProgram(programMap);
      } else {
        setCourseHasProgram({});
      }
      setError(null);
      setCoursesFetched(true);
    } catch (err) {
      swalError(err.error);
    } finally {
      setLoading(false);
    }
  }, [user.instituteId, setCourseHasProgram, isCOPOEnabled]);

  const fetchPeriods = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/period/get-all/${user.instituteId}`,
        {
          credentials: "include",
        },
      );
      if (!response) {
        setInitialPeriodDetermined(true);
        return;
      }
      if (!response.ok) throw new Error("Failed to fetch periods");
      const data = await response.json();
      setPeriods(data);
      setError(null);
      setPeriodsFetched(true);

      if (data.length > 0) {
        const ongoing = data.find((p) => p.status === "Ongoing");
        const selected = ongoing || data[0];
        const selectedId = selected.id || selected._id;

        setNewClass((prev) => ({
          ...prev,
          periodId: selectedId,
          enrollmentEndDate: selected.enrollmentEndDate
            ? userDate(selected.enrollmentEndDate)
            : "",
        }));

        setSelectedPeriod({
          value: selectedId,
          label: `${selected.term.charAt(0).toUpperCase() + selected.term.slice(1)} ${selected.year}`
        });
      }
      setInitialPeriodDetermined(true);
    } catch (err) {
      setError(err.message);
      swalError(err.error);
      setInitialPeriodDetermined(true);
    } finally {
      setLoading(false);
    }
  }, [user.instituteId]);

  useEffect(() => {
    fetchCourses();
    fetchPeriods();
  }, [fetchCourses, fetchPeriods]);

  useEffect(() => {
    if (initialPeriodDetermined) {
      fetchClasses();
    }
  }, [initialPeriodDetermined, selectedPeriod?.value, fetchClasses]);

  useEffect(() => {
    if (classes.length > 0 && Object.keys(courseHasProgram).length > 0) {
      classes.forEach((cls) => {
        const course = courses.find((c) => c._id === cls.courseId);
        const hasProgram = course
          ? courseHasProgram[course.id || course._id]
          : false;
        if (hasProgram) {
          fetchClassAttainment(cls.id || cls._id);
        }
      });
    }
  }, [classes, courseHasProgram, courses, fetchClassAttainment]);

  const handleCreateClick = () => {
    const ongoing = periods.find((p) => p.status === "Ongoing");
    setNewClass({
      courseId: "",
      periodId: ongoing ? ongoing.id || ongoing._id : "",
      instructorId: user._id,
      enrollmentEndDate: ongoing ? userDate(ongoing.enrollmentEndDate) : "",

      name: "",

      level: null,
    });
    setIsCreateModalOpen(true);
  };

  const handleSelectChange = (selectedOption, { name }) => {
    if (name === "periodId") {
      const selectedPeriod = periods.find(
        (p) => p.id === selectedOption.value || p._id === selectedOption.value,
      );
      const enrollmentEndDate = selectedPeriod
        ? userDate(selectedPeriod.enrollmentEndDate)
        : "";
      setNewClass((prev) => ({
        ...prev,
        periodId: selectedOption ? selectedOption.value : "",
        enrollmentEndDate,
      }));
    } else if (name === "level") {
      setNewClass((prev) => ({
        ...prev,
        level: selectedOption ? selectedOption.value : "",
      }));
    } else {
      setNewClass((prev) => ({
        ...prev,
        [name]: selectedOption ? selectedOption.value : "",
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewClass((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    setFormError("");
    if (courses.length > 0 && !newClass.courseId) {
      setFormError("Please select a Course.");
      return;
    }
    if (courses.length === 0 && !newClass.level) {
      setFormError("Please select a Level.");
      return;
    }

    setIsCreating(true);
    try {
      let levelToSend = null;
      if (courses.length > 0 && newClass.courseId) {
        const selectedCourse = courses.find(
          (c) => c.id === newClass.courseId || c._id === newClass.courseId,
        );
        levelToSend = selectedCourse?.level;
      } else {
        levelToSend = newClass.level;
      }
      const classToCreate = {
        ...newClass,
        enrollmentEndDate: newClass.enrollmentEndDate
          ? convertToUserTimezone(newClass.enrollmentEndDate)
          : "",
        name: newClass.className,

        level: levelToSend,
        periodId: newClass.periodId || null,
      };
      if (courses.length === 0) {
        delete classToCreate.courseId;
      }
      const response = await fetchWithAuth(`${serverUrl}/api/class/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(classToCreate),
        credentials: "include",
      });
      if (!response) return;
      if (!response.ok) {
        let errorText;
        try {
          errorText = await response.json();
        } catch {
          errorText = await response.text();
        }
        throw new Error(
          errorText.message || "Failed to create class. Please try again.",
        );
      }
      const createdClass = await response.json();
      await fetchClasses();
      setIsCreateModalOpen(false);
      swalSuccess("Class created successfully!");
      // Navigate to the newly created class
      if (createdClass && createdClass._id) {
        navigate(`/instructor/manage-class/${createdClass._id}/enrollments`, {
          state: { combinedTitle: createdClass.name || createdClass.className },
        });
      }
    } catch (error) {
      setFormError(
        error.message || "Failed to create class. Please try again.",
      );
      swalError("Failed to create class!" + (error.error || error.message));
    } finally {
      setIsCreating(false);
    }
  };

  const handlePeriodDropdownOpen = () => {
    if (!periodsFetched) {
      fetchPeriods();
    }
  };

  const handleCourseDropdownOpen = () => {
    if (!coursesFetched) {
      fetchCourses();
    }
  };

  const handleDeleteClass = async (classId, e) => {
    e.stopPropagation();
    const result = await swalConfirm(
      "Are you sure you want to delete this class?",
      "",
      "Yes, delete it!",
      "No, Cancel",
    );

    if (result.isConfirmed) {
      setDeletingClasses((prev) => new Set(prev).add(classId));
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/class/delete/${classId}`,
          {
            method: "DELETE",
            credentials: "include",
          },
        );
        if (!response) return;
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to delete class.");
        }
        swalSuccess("Class deleted successfully!");
        setClasses((prevClasses) =>
          prevClasses.filter((cls) => cls.id !== classId),
        );
      } catch (err) {
        swalError(err.message || "Failed to delete class.");
      } finally {
        setDeletingClasses((prev) => {
          const newSet = new Set(prev);
          newSet.delete(classId);
          return newSet;
        });
      }
    }
  };

  const handleEditClick = (cls) => {
    const formattedEnrollmentEndDate = cls.enrollmentEndDate
      ? userDate(cls.enrollmentEndDate)
      : "";
    const currentLevel =
      cls.level ||
      (courses.length > 0 && cls.courseId
        ? courses.find((c) => c.id === cls.courseId || c._id === cls.courseId)
            ?.level
        : null);
    setEditClassData({
      ...cls,
      enrollmentEndDate: formattedEnrollmentEndDate,
      name: cls.name || "",
      level: currentLevel,
    });
    const hasIndirect =
      cls.indirectCOAttainments && cls.indirectCOAttainments.length > 0;
    setIsIndirectAttainment(hasIndirect);
    setInitialEditClassData({
      periodId: cls.periodId,
      courseId: cls.courseId,
      enrollmentEndDate: formattedEnrollmentEndDate,

      name: cls.name || "",

      level: currentLevel,
      indirectCOAttainments: cls.indirectCOAttainments || [],
      isIndirectAttainment: hasIndirect,
    });
    setIsEditModalOpen(true);
    setIsEditChanged(false);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditClassData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Helper function to compare indirectCOAttainments arrays
  const areIndirectCOAttainmentsEqual = (arr1, arr2) => {
    if (!arr1 && !arr2) return true;
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;

    return arr1.every((item1, index) => {
      const item2 = arr2[index];
      return (
        item1.coLevel === item2.coLevel &&
        item1.attainment === item2.attainment &&
        item1.source === item2.source
      );
    });
  };

  // Updated useEffect for tracking changes
  useEffect(() => {
    if (editClassData && initialEditClassData) {
      const fieldsToCheck = [
        "periodId",
        "courseId",
        "enrollmentEndDate",

        "name",

        "level",
      ];

      // Check simple fields
      const simpleFieldsChanged = fieldsToCheck.some(
        (key) => editClassData[key] !== initialEditClassData[key],
      );

      // Check indirectCOAttainments
      const indirectCOChanged = !areIndirectCOAttainmentsEqual(
        editClassData.indirectCOAttainments,
        initialEditClassData.indirectCOAttainments,
      );
      // Check Indirect CO Attainment toggle state
      const isIndirectToggleChanged =
        isIndirectAttainment !== initialEditClassData.isIndirectAttainment;
      setIsEditChanged(
        simpleFieldsChanged || indirectCOChanged || isIndirectToggleChanged,
      );
    } else {
      setIsEditChanged(false);
    }
  }, [
    editClassData,
    initialEditClassData,
    courses.length,
    isIndirectAttainment,
  ]);

  const handleEditSelectChange = (selectedOption, { name }) => {
    const value = selectedOption ? selectedOption.value : "";
    let updatedData = { [name]: value };

    if (name === "periodId") {
      const selectedPeriod = periods.find(
        (p) => p.id === selectedOption.value || p._id === selectedOption.value,
      );
      const enrollmentEndDate = selectedPeriod
        ? userDate(selectedPeriod.enrollmentEndDate)
        : "";
      updatedData.enrollmentEndDate = enrollmentEndDate;
    } else if (name === "courseId" && courses.length > 0) {
      const selectedCourse = courses.find(
        (c) => c.id === value || c._id === value,
      );
      updatedData.level = selectedCourse?.level;
    } else if (name === "level") {
      updatedData.level = value;
    }

    setEditClassData((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  const handleEditClass = async (e) => {
    e.preventDefault();
    setFormError("");
    if (courses.length === 0 && !editClassData.level) {
      setFormError("Please select a Level.");
      return;
    }

    setIsUpdating(true);
    try {
      const changedFields = Object.keys(initialEditClassData).filter((key) => {
        if (key === "isIndirectAttainment") return false;
        if (key === "level" && courses.length > 0) {
          const initialCourse = courses.find(
            (c) =>
              c.id === initialEditClassData.courseId ||
              c._id === initialEditClassData.courseId,
          );
          const currentCourse = courses.find(
            (c) =>
              c.id === editClassData.courseId ||
              c._id === editClassData.courseId,
          );
          return initialCourse?.level !== currentCourse?.level;
        }
        return editClassData[key] !== initialEditClassData[key];
      });

      const classToUpdate = changedFields.reduce((acc, key) => {
        if (key === "enrollmentEndDate" && editClassData[key]) {
          acc[key] = convertToUserTimezone(editClassData[key]);
        } else if (key === "level") {
          if (courses.length > 0 && editClassData.courseId) {
            const selectedCourse = courses.find(
              (c) =>
                c.id === editClassData.courseId ||
                c._id === editClassData.courseId,
            );
            acc[key] = selectedCourse?.level;
          } else {
            acc[key] = editClassData[key];
          }
        } else {
          acc[key] = editClassData[key];
        }
        return acc;
      }, {});

      if (
        courses.length > 0 &&
        changedFields.includes("courseId") &&
        !changedFields.includes("level")
      ) {
        const selectedCourse = courses.find(
          (c) =>
            c.id === editClassData.courseId || c._id === editClassData.courseId,
        );
        classToUpdate.level = selectedCourse?.level;
      }

      if (courses.length === 0) {
        delete classToUpdate.courseId;
      }

      if (isIndirectAttainment !== initialEditClassData.isIndirectAttainment) {
        classToUpdate.indirectCOAttainments = isIndirectAttainment
          ? editClassData.indirectCOAttainments || []
          : [];
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/class/update/${editClassData.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(classToUpdate),
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) {
        let errorText;
        try {
          errorText = await response.json();
        } catch {
          errorText = await response.text();
        }
        throw new Error(
          errorText.message || "Failed to edit class. Please try again.",
        );
      }
      await fetchClasses();
      setEditClassData(null);
      setInitialEditClassData(null);
      setIsEditModalOpen(false);
      setIsEditChanged(false);
      swalSuccess("Class edited successfully!");
    } catch (error) {
      setFormError(error.message || "Failed to edit class. Please try again.");
      swalError("Failed to edit class!" + (error.error || error.message));
    } finally {
      setIsUpdating(false);
    }
  };

  const courseOptions = courses.map((course) => ({
    value: course.id || course._id,
    label:
      `${course.code.charAt(0).toUpperCase() + course.code.slice(1)} - ` +
      course.name,
  }));

  const periodOptions = periods.map((period) => ({
    value: period.id || period._id,
    label:
      `${period.term.charAt(0).toUpperCase() + period.term.slice(1)} ` +
      period.year,
  }));

  const isLanguageInst =
    institute?.category === "LANGUAGE_LEARNING_INSTITUTE" ||
    user?.category === "LANGUAGE_LEARNING_INSTITUTE" ||
    user?.institute?.category === "LANGUAGE_LEARNING_INSTITUTE";

  const uniqueLevels = React.useMemo(() => {
    const set = new Set();
    classes.forEach((cls) => {
      if (cls.level) set.add(cls.level);
    });
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
    );
  }, [classes]);

  const filteredClassList = classes
    .filter((cls) => !cls.isLevelPlacementZone)
    .filter((cls) => {
      if (!selectedLevelFilter || selectedLevelFilter.value === "all") return true;
      return cls.level === selectedLevelFilter.value;
    });

  let cardsData = [];

  if (!user.sourceSystem && !isLanguageInst) {
    const actionCard = {
      id: "action-card",
      title: "New Class",
      onAction: handleCreateClick,
      isActionCard: true,
      disabled:
        isCreating ||
        isUpdating ||
        deletingClasses.size > 0 ||
        syncingClasses.size > 0,
    };
    cardsData.push(actionCard);
  }

  cardsData = [
    ...cardsData,
    ...filteredClassList
      .filter((cls) => !cls.isLevelPlacementZone)
      .map((cls) => {
      const course = courses.find((c) => c._id === cls.courseId);
      const hasProgram = course
        ? courseHasProgram[course.id || course._id]
        : false;
      const combinedTitle = cls.name || cls.course?.name || "Loading...";

      const cardId = String(cls.id || cls._id);
      return {
        id: cardId,
        title: combinedTitle,
        level: cls.level || null,
        levelIcon: isLanguageInst ? "fa-language" : "fa-graduation-cap",
        description: (
          <>
            {false && isLanguageInst && cls.level && (
              <div style={{ marginBottom: "6px" }}>
                <span
                  className={`cefr-badge cefr-${String(cls.level)
                    .toLowerCase()
                    .replace(/\./g, "-")}`}
                >
                  <i className="fa-solid fa-language cefr-badge-icon"></i>
                  {cls.level}
                </span>
              </div>
            )}
            {course && course.name && <p>Course Name: {course.name}</p>}

            {!isLanguageInst && institute && institute.curriculum && (
              <p>Curriculum: {institute.curriculum}</p>
            )}

            {cls.code && <p>Enrollment Code: {cls.code}</p>}
            <p>Students Enrolled: {cls.studentCount}</p>

            {attainmentMap[cardId] !== undefined &&
              hasProgram &&
              attainmentMap[cardId].overall !== "N/A" && (
                <p title={attainmentMap[cardId]?.details || ""}>
                  Attainment: {attainmentMap[cardId]?.overall}
                </p>
              )}
          </>
        ),
        onClick: () => {
          navigate(`/instructor/manage-class/${cardId}`, {
            state: { combinedTitle },
          });
        },
        children: (
          <div className="card__actions">
            {!user?.sourceSystem ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(cls);
                }}
                className="btn-edit"
                aria-label="Edit Class"
                disabled={
                  isCreating ||
                  isUpdating ||
                  deletingClasses.has(cardId) ||
                  syncingClasses.has(cardId)
                }
              >
                <img src="/imgs/pencil_icon.svg" alt="edit-btn" width={25} />
              </button>
            ) : (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  const button = e.currentTarget;
                  button.classList.add("syncing");
                  await handleSyncClass(cls.id || cls._id);
                  button.classList.remove("syncing");
                  button.classList.add("sync-success");
                  setTimeout(() => {
                    button.classList.remove("sync-success");
                  }, 600);
                }}
                className="btn-edit"
                aria-label="Sync Class"
                disabled={
                  isCreating ||
                  isUpdating ||
                  deletingClasses.has(cardId) ||
                  syncingClasses.has(cardId)
                }
              >
                <div className="sync-icon-wrapper">
                  <img
                    src={
                      user.sourceSystem === "Canvas"
                        ? "/imgs/canvas_logo.png"
                        : "/imgs/google_classroom_logo.png"
                    }
                    alt={`${user.sourceSystem} logo`}
                    className={`platform-logo ${
                      syncingClasses.has(cardId) ? "syncing" : ""
                    }`}
                  />
                  {syncingClasses.has(cardId) ? (
                    <div className="sync-spinner">
                      <i className="fa-solid fa-circle-notch fa-spin"></i>
                    </div>
                  ) : (
                    <div className="sync-icon">
                      <i className="fa-solid fa-arrows-rotate"></i>
                    </div>
                  )}
                </div>
              </button>
            )}
            {hasProgram && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  calculateClassAttainment(cardId);
                }}
                className="btn-attainment"
                title="Compute Attainment"
                aria-label="Compute Attainment"
                disabled={
                  isCreating ||
                  isUpdating ||
                  deletingClasses.has(cardId) ||
                  syncingClasses.has(cardId) ||
                  computingAttainment.has(cardId)
                }
              >
                {computingAttainment.has(cardId) ? (
                  <i className="fa-solid fa-spinner fa-spin"></i>
                ) : (
                  <i className="fa-solid fa-square-poll-horizontal"></i>
                )}
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClass(cardId, e);
              }}
              className="btn-delete"
              aria-label="Delete Class"
              disabled={
                isCreating ||
                isUpdating ||
                deletingClasses.has(cardId) ||
                syncingClasses.has(cardId) ||
                computingAttainment.has(cardId)
              }
            >
              {deletingClasses.has(cardId) ? (
                <i className="fa-solid fa-spinner fa-spin"></i>
              ) : (
                <img src="/imgs/trash_icon.svg" alt="delete-btn" width={25} />
              )}
            </button>
          </div>
        ),
        enrollmentEndDate: userDate(cls.enrollmentEndDate),
      };
    }),
  ];

  const syncGoogleClassroomClass = async (classId) => {
    setSyncingClasses((prev) => new Set(prev).add(classId));
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/google-classroom/sync-data/class/${classId}`,
        {
          method: "get",
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to sync class with Google Classroom.",
        );
      }
      swalSuccess("Class synced successfully!");
      fetchClasses();
    } catch (err) {
      handleGoogleError(err);
    } finally {
      setSyncingClasses((prev) => {
        const newSet = new Set(prev);
        newSet.delete(classId);
        return newSet;
      });
    }
  };

  const syncCanvasClass = async (classId) => {
    setSyncingClasses((prev) => new Set(prev).add(classId));
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/canvas/sync-data/class/${classId}`,
        {
          method: "get",
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to sync class with Canvas.",
        );
      }
      swalSuccess("Class synced successfully!");
      fetchClasses();
    } catch (err) {
      console.error(err);
      swalError(err.message);
    } finally {
      setSyncingClasses((prev) => {
        const newSet = new Set(prev);
        newSet.delete(classId);
        return newSet;
      });
    }
  };

  // Source system and class sync method mapping
  const sourceSystemClassSyncMap = {
    "Google Classroom": syncGoogleClassroomClass,
    Canvas: syncCanvasClass,
  };

  const handleSyncClass = async (classId) => {
    const refreshFn = sourceSystemClassSyncMap[user.sourceSystem];
    if (!refreshFn) return;
    return refreshFn(classId);
  };

  const handleGoogleError = (err) => {
    console.error(err);
    if (err.message === "invalid_grant") {
      swalError(
        "Session Expired. Please register again with Google Classroom.",
      );
      handleInvalidGrant();
      return;
    }
    swalError(err.message);
  };

  const handleInvalidGrant = async () => {
    try {
      const response = await fetchWithAuth(`${serverUrl}/api/auth/signout`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to sign out");
      }

      logout();
      window.location.href = `${serverUrl}/api/oauth/get-token?instituteCode=&instituteName=&role=${user.role}&isGoogleClassroomUser=true&isGoogleSSOUser=false`;
    } catch (error) {
      swalError(error.message || "Failed to sign out");
    }
  };

  const calculateClassAttainment = async (classId) => {
    setComputingAttainment((prev) => new Set(prev).add(classId));
    try {
      // Trigger compute on server
      const computeRes = await fetchWithAuth(
        `${serverUrl}/api/class/computeAttainment/${classId}`,
        { method: "PUT", credentials: "include" },
      );
      if (!computeRes) return;
      if (!computeRes.ok) {
        const err = await computeRes.json().catch(() => ({}));
        throw new Error(err.message || "Failed to compute attainment");
      }
      // Fetch aggregated attainment summary
      const aggRes = await fetchWithAuth(
        `${serverUrl}/api/class/getAttainment/${classId}`,
        { credentials: "include" },
      );
      if (!aggRes) return;
      if (!aggRes.ok) {
        const err = await aggRes.json().catch(() => ({}));
        throw new Error(err.message || "Failed to fetch attainment summary");
      }
      const aggData = await aggRes.json();
      if (
        aggData &&
        aggData.attainment &&
        Array.isArray(aggData.attainment) &&
        aggData.attainment.length > 0
      ) {
        const overall = Number(aggData.overallAverage) || 0;
        const roundedOverall = Number(overall.toFixed(2));
        const details = aggData.attainment
          .map((r) => `${r.coLevel || "N/A"}: ${r.totalCOAttainment}`)
          .join(", ");
        setAttainmentMap((prev) => {
          const updated = {
            ...prev,
            [classId]: { overall: roundedOverall, details },
          };
          localStorage.setItem("classAttainmentMap", JSON.stringify(updated));
          return updated;
        });
        swalSuccess("Attainment computed successfully.");
      } else {
        setAttainmentMap((prev) => {
          const updated = {
            ...prev,
            [classId]: { overall: "N/A", details: "" },
          };
          localStorage.setItem("classAttainmentMap", JSON.stringify(updated));
          return updated;
        });
        swalWarning("No attainment data available for this class.");
      }
      // Refresh classes to pick up any changes
      fetchClasses();
    } catch (err) {
      swalError(err.message || "Error computing attainment");
    } finally {
      setComputingAttainment((prev) => {
        const newSet = new Set(prev);
        newSet.delete(classId);
        return newSet;
      });
    }
  };

  const renderIndirectCOAttainmentsTable = (isEdit = false) => {
    const classData = isEdit ? editClassData : newClass;
    const isSubmitting = isEdit ? isUpdating : isCreating;

    const course = courses.find(
      (c) => c._id.toString() === classData.courseId.toString(),
    );
    const courseOutcomes = course?.outcomes || [];

    // Create rows based on course outcomes
    const rows = courseOutcomes.map((outcome) => {
      // Find existing attainment for this CO
      const existingAttainment = (classData.indirectCOAttainments || []).find(
        (att) => att.coLevel === outcome.code,
      );

      return {
        coLevel: outcome.code,
        attainment: existingAttainment?.attainment || "",
        source: existingAttainment?.source || "",
      };
    });

    const headers = [
      {
        key: "coLevel",
        label: "CO Level",
        width: "150px",
        required: true,
      },
      {
        key: "attainment",
        label: "Attainment",
        width: "150px",
        required: true,
        placeholder: "e.g., 0.75",
      },
      {
        key: "source",
        label: "Source",
        width: "1fr",
        required: false,
        placeholder: "e.g., Course Exit Survey",
      },
    ];

    const renderCell = (header, row, rowIndex) => {
      switch (header.key) {
        case "coLevel":
          return (
            <input
              type="text"
              value={row.coLevel}
              required
              disabled
              className="modal-table-input"
              style={{
                backgroundColor: "#f5f5f5",
                cursor: "not-allowed",
                color: "#666",
              }}
            />
          );
        case "attainment":
          return (
            <input
              type="number"
              value={row.attainment}
              onChange={(e) =>
                handleIndirectCOChange(
                  row.coLevel,
                  "attainment",
                  parseFloat(e.target.value) || "",
                  isEdit,
                )
              }
              required
              placeholder={header.placeholder}
              disabled={isSubmitting}
              min="0"
              max="3"
              step="0.25"
              className="modal-table-input"
            />
          );
        case "source":
          return (
            <input
              type="text"
              value={row.source}
              onChange={(e) =>
                handleIndirectCOChange(
                  row.coLevel,
                  "source",
                  e.target.value,
                  isEdit,
                )
              }
              placeholder={header.placeholder}
              disabled={isSubmitting}
              className="modal-table-input"
            />
          );
        default:
          return null;
      }
    };

    return (
      <div className="form-group" style={{ marginTop: "20px" }}>
        <label>Indirect CO Attainments</label>
        {courseOutcomes.length === 0 ? (
          <p style={{ color: "#666", fontStyle: "italic" }}>
            Please select a course first to view CO attainments
          </p>
        ) : (
          <ModalTableInput
            headers={headers}
            rows={rows}
            onRowChange={() => {}}
            onRowRemove={() => {}}
            onRowAdd={null} // No add button needed
            renderCell={renderCell}
            addButtonText="" // No add button
          />
        )}
      </div>
    );
  };
  // Updated handler function - now uses coLevel as identifier instead of index
  const handleIndirectCOChange = (coLevel, field, value, isEdit) => {
    const target = isEdit ? editClassData : newClass;
    const attainments = [...(target.indirectCOAttainments || [])];

    // Find existing entry for this CO
    const existingIndex = attainments.findIndex(
      (att) => att.coLevel === coLevel,
    );

    if (existingIndex >= 0) {
      // Update existing entry
      attainments[existingIndex] = {
        ...attainments[existingIndex],
        [field]: value,
      };
    } else {
      // Create new entry
      attainments.push({
        coLevel: coLevel,
        attainment: field === "attainment" ? value : "",
        source: field === "source" ? value : "",
      });
    }

    if (isEdit) {
      setEditClassData({
        ...editClassData,
        indirectCOAttainments: attainments,
      });
    } else {
      setNewClass({ ...newClass, indirectCOAttainments: attainments });
    }
  };

  return (
    <div>
      <div className="instructor-home">
        {periods.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              width: "100%",
              position: "relative",
              right: "2rem",
              top: "1rem",
            }}
          >
            {uniqueLevels.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", marginRight: "1rem" }}>
                <span style={{ marginRight: "0.3rem" }}>Level:</span>
                <div style={{ minWidth: "160px" }}>
                  <Select
                    value={selectedLevelFilter}
                    onChange={(selected) => setSelectedLevelFilter(selected)}
                    options={[{ value: "all", label: "All Levels" }, ...uniqueLevels.map((lvl) => ({ value: lvl, label: lvl }))]}
                    placeholder="All Levels"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    isClearable={false}
                  />
                </div>
              </div>
            )}
            <span style={{ marginRight: "0.2rem" }}>Period:</span>
            <div style={{ minWidth: "200px" }}>
              <Select
                value={selectedPeriod}
                onChange={(selected) => {
                  setSelectedPeriod(selected);
                }}
                options={[{ value: 'all', label: 'All' }, ...periodOptions]}
                placeholder="Select Period"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>
        )}
        {error && <div className="error-message">{error}</div>}
        {loading ? (
          <Loader style={{ fontSize: "50px" }} />
        ) : (
          <CardList cards={cardsData} />
        )}
      </div>
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => !isCreating && setIsCreateModalOpen(false)}
      >
        <h2>{isLanguageInst ? "Create New Batch" : "Create New Class"}</h2>
        <form onSubmit={handleCreateClass}>
          {courses.length > 0 ? (
            <div className="form-group">
              <label htmlFor="courseId">
                <i className="fa-solid fa-asterisk requiredIcon"></i> Course
              </label>
              <Select
                id="courseId"
                name="courseId"
                options={courseOptions}
                value={
                  newClass.courseId
                    ? courseOptions.find(
                        (option) => option.value === newClass.courseId,
                      ) || null
                    : null
                }
                onChange={(selectedOption) => {
                  const selectedCourse = courses.find(
                    (course) =>
                      course.id === selectedOption?.value ||
                      course._id === selectedOption?.value,
                  );
                  setNewClass({
                    ...newClass,
                    courseId: selectedOption ? selectedOption.value : "",
                    className: selectedCourse ? selectedCourse.name : "",
                  });
                }}
                onMenuOpen={handleCourseDropdownOpen}
                isClearable
                placeholder="Select or search course..."
                required
                getOptionValue={(option) => option.value || ""}
                isOptionSelected={(option) =>
                  option.value === newClass.courseId
                }
                formatOptionLabel={(option) =>
                  option.label ? option.label : ""
                }
                isDisabled={isCreating}
              />
              <small>You can search for a course by name or code.</small>
            </div>
          ) : null}
          <div className="form-group">
            <label htmlFor="className">
              <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
              {isLanguageInst ? "Batch Name / Number" : "Class Name"}
            </label>
            {institute?.category === "K12_SCHOOL" ? (
              <Select
                id="className"
                name="className"
                options={subjects}
                value={
                  newClass.className
                    ? subjects.find((opt) => opt.value === newClass.className) ||
                      { value: newClass.className, label: newClass.className }
                    : null
                }
                onChange={(selectedOption) => {
                  setNewClass((prev) => ({
                    ...prev,
                    className: selectedOption ? selectedOption.value : "",
                  }));
                }}
                isClearable
                placeholder="Select class name..."
                required
                isDisabled={isCreating}
              />
            ) : (
              <input
                type="text"
                id="className"
                name="className"
                placeholder={
                  isLanguageInst
                    ? "e.g. Batch 101, German A1 - Morning Batch"
                    : "Enter class name (e.g. Science A, Math B etc.)"
                }
                value={newClass.className || ""}
                onChange={handleInputChange}
                required
                disabled={isCreating}
              />
            )}
          </div>

          {courses.length === 0 && (
            <div className="form-group">
              <label htmlFor="level">
                <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                {isLanguageInst ? "CEFR Level" : "Level"}
              </label>
              <Select
                id="level"
                name="level"
                options={levels}
                value={
                  newClass.level
                    ? levels.find(
                        (option) => option.value === newClass.level,
                      ) || null
                    : null
                }
                onChange={handleSelectChange}
                placeholder={isLanguageInst ? "Select CEFR Level" : "Select Level"}
                isClearable
                required={true}
                isDisabled={isCreating}
              />
            </div>
          )}

          {periods.length > 0 ? (
            <div className="form-group">
              <label htmlFor="periodId">{isLanguageInst ? "Term / Period" : "Period"}</label>
              <Select
                id="periodId"
                name="periodId"
                options={periodOptions}
                value={
                  newClass.periodId
                    ? periodOptions.find(
                        (option) => option.value === newClass.periodId,
                      ) || null
                    : null
                }
                onChange={handleSelectChange}
                onMenuOpen={handlePeriodDropdownOpen}
                placeholder="Select or search period..."
                getOptionValue={(option) => option.value || ""}
                isOptionSelected={(option) =>
                  option.value === newClass.periodId
                }
                formatOptionLabel={(option) =>
                  option.label ? option.label : ""
                }
                isDisabled={isCreating}
              />
              <small>You can search for a period by term or year.</small>
            </div>
          ) : null}
          {formError && <div className="error-message">{formError}</div>}
          {isCreating ? (
            <Loader />
          ) : (
            <button type="submit" className="btn-submit">
              {isLanguageInst ? "Create Batch" : "Create Class"}
            </button>
          )}
        </form>
      </Modal>
      {editClassData && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            if (!isUpdating) {
              setIsIndirectAttainment(false);
              setIsEditModalOpen(false);
              setEditClassData(null);
              setInitialEditClassData(null);
              setIsEditChanged(false);
              setFormError("");
            }
          }}
          maxWidth={isIndirectAttainment ? "50%" : undefined}
        >
          <h2>{isLanguageInst ? "Edit Batch" : "Edit Class"}</h2>
          <form onSubmit={handleEditClass}>
            {courses.length > 0 ? (
              <div className="form-group">
                <label htmlFor="courseId">Course</label>
                <Select
                  id="courseId"
                  name="courseId"
                  options={courseOptions}
                  value={
                    editClassData.courseId
                      ? courseOptions.find(
                          (option) => option.value === editClassData.courseId,
                        ) || null
                      : null
                  }
                  onChange={(selectedOption) => {
                    const selectedCourse = courses.find(
                      (course) =>
                        course.id === selectedOption?.value ||
                        course._id === selectedOption?.value,
                    );
                    handleEditSelectChange(selectedOption, {
                      name: "courseId",
                    });
                    setEditClassData((prev) => ({
                      ...prev,
                      className: selectedCourse ? selectedCourse.name : "",
                    }));
                  }}
                  onMenuOpen={handleCourseDropdownOpen}
                  isClearable
                  placeholder="Select or search course..."
                  getOptionValue={(option) => option.value || ""}
                  isOptionSelected={(option) =>
                    option.value === editClassData.courseId
                  }
                  formatOptionLabel={(option) =>
                    option.label ? option.label : ""
                  }
                  isDisabled={isUpdating}
                />
                <small>You can search for a course by name or code.</small>
              </div>
            ) : null}
            <div className="form-group">
              <label htmlFor="className">
                <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                {isLanguageInst ? "Batch Name / Number" : "Class Name"}
              </label>
              {institute?.category === "K12_SCHOOL" ? (
                <Select
                  id="className"
                  name="name"
                  options={subjects}
                  value={
                    editClassData.name
                      ? subjects.find((opt) => opt.value === editClassData.name) ||
                        { value: editClassData.name, label: editClassData.name }
                      : null
                  }
                  onChange={(selectedOption) => {
                    handleEditSelectChange(selectedOption, { name: "name" });
                  }}
                  isClearable
                  placeholder="Select class name..."
                  isDisabled={isUpdating}
                />
              ) : (
                <input
                  type="text"
                  id="className"
                  name="name"
                  placeholder={
                    isLanguageInst
                      ? "e.g. Batch 101, German A1 - Morning Batch"
                      : "Enter class name"
                  }
                  value={editClassData.name || ""}
                  onChange={handleEditInputChange}
                  required
                  disabled={isUpdating}
                />
              )}
            </div>

            {courses.length === 0 && (
              <div className="form-group">
                <label>{isLanguageInst ? "CEFR Level" : "Level"}</label>
                <Select
                  id="level"
                  name="level"
                  options={levels}
                  value={
                    editClassData.level
                      ? levels.find(
                          (option) => option.value === editClassData.level,
                        ) || null
                      : null
                  }
                  onChange={(selectedOption) =>
                    handleEditSelectChange(selectedOption, { name: "level" })
                  }
                  isClearable
                  placeholder={isLanguageInst ? "Select CEFR level..." : "Select level..."}
                  isDisabled={isUpdating}
                />
              </div>
            )}
            {periods.length > 0 ? (
              <div className="form-group">
                <label htmlFor="periodId">Period</label>
                <Select
                  id="periodId"
                  name="periodId"
                  options={periodOptions}
                  value={
                    editClassData.periodId
                      ? periodOptions.find(
                          (option) => option.value === editClassData.periodId,
                        ) || null
                      : null
                  }
                  onChange={handleEditSelectChange}
                  onMenuOpen={handlePeriodDropdownOpen}
                  placeholder="Select or search period..."
                  required
                  getOptionValue={(option) => option.value || ""}
                  isOptionSelected={(option) =>
                    option.value === editClassData.periodId
                  }
                  formatOptionLabel={(option) =>
                    option.label ? option.label : ""
                  }
                  isDisabled={isUpdating}
                />
                <small>You can search for a period by term or year.</small>
              </div>
            ) : null}
            {courseHasProgram[editClassData.courseId] && (
              <div className="form-group">
                <label>Indirect CO Attainment</label>
                <label className="solution-toggle">
                  <input
                    type="checkbox"
                    name="isIndirectAttainment"
                    checked={isIndirectAttainment}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsIndirectAttainment(checked);
                    }}
                    disabled={isUpdating}
                  />
                  <span className="toggle-track">
                    <span className="toggle-indicator"></span>
                  </span>
                  <span className="toggle-label-text">
                    {!isIndirectAttainment
                      ? "Provide Indirect CO Attainment For the class."
                      : ""}
                  </span>
                </label>
              </div>
            )}

            {/* Conditional Indirect CO Attainment Table */}
            {isIndirectAttainment && (
              <div className="table-container">
                {renderIndirectCOAttainmentsTable(true)}
              </div>
            )}

            {formError && <div className="error-message">{formError}</div>}
            {isUpdating ? (
              <Loader />
            ) : (
              <button
                type="submit"
                className="btn-submit"
                disabled={!isEditChanged || isUpdating}
                onClick={(e) => {
                  if (!isEditChanged && !isUpdating) {
                    e.preventDefault();
                    swalWarning("No changes detected.");
                  }
                }}
              >
                {isLanguageInst ? "Update Batch" : "Save"}
              </button>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
};

export default InstructorClasses;
